/**
 * Texto de template de WhatsApp: variáveis nomeadas de um lado, o formato
 * posicional da Meta do outro.
 *
 * A Meta só aceita `{{1}}`, `{{2}}` — sem nomes. Escrever assim é possível, mas
 * caro: quem edita o texto seis meses depois não sabe o que é `{{3}}`, e trocar
 * a ordem de duas frases desalinha silenciosamente todos os envios seguintes.
 * O sistema não erra; o participante recebe o nome da empresa onde deveria vir
 * o do evento.
 *
 * Aqui o texto é AUTORADO com nome — `Olá {{nome_participante}}` — e traduzido
 * para posicional só na hora de submeter. `paraFormatoMeta` devolve, junto, a
 * ordem em que os valores precisam ser mandados no envio. É o mesmo cálculo dos
 * dois lados, então eles não têm como discordar.
 */

/** `{{nome_da_variavel}}` — nome começa por letra, sem acento. */
const VARIAVEL = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;

/** Qualquer `{{...}}`, inclusive o malformado — usado para achar sobras. */
const CHAVES = /\{\{[^}]*\}\}/g;

/** Limites da Cloud API (mudam raramente, mas mudam — ver docs da Meta). */
export const LIMITES_TEMPLATE = {
  header: 60,
  body: 1024,
  footer: 60,
  botoes: 3,
  textoBotao: 25,
} as const;

export interface BotaoTemplate {
  tipo: 'RESPOSTA_RAPIDA' | 'URL' | 'TELEFONE';
  texto: string;
  /** URL ou telefone. Ignorado em RESPOSTA_RAPIDA. */
  valor?: string;
}

export interface PartesTemplate {
  header?: string | null;
  body: string;
  footer?: string | null;
  botoes?: BotaoTemplate[] | null;
}

/** Nomes das variáveis, na ordem de aparição e sem repetir. */
export function extrairVariaveis(texto: string | null | undefined): string[] {
  if (!texto) return [];

  const vistas: string[] = [];
  for (const [, nome] of texto.matchAll(VARIAVEL)) {
    if (nome && !vistas.includes(nome)) vistas.push(nome);
  }
  return vistas;
}

/**
 * Converte para o formato que a Meta aceita.
 *
 * Devolve também a ordem dos nomes: é ela que o envio usa para montar os
 * `components`, e é por isso que a conversão não pode ser feita "na mão" em
 * dois lugares diferentes.
 */
export function paraFormatoMeta(texto: string | null | undefined): {
  texto: string;
  variaveis: string[];
} {
  const variaveis = extrairVariaveis(texto);
  if (!texto) return { texto: '', variaveis };

  const convertido = texto.replace(VARIAVEL, (original, nome: string) => {
    const posicao = variaveis.indexOf(nome);
    return posicao === -1 ? original : `{{${posicao + 1}}}`;
  });

  return { texto: convertido, variaveis };
}

/**
 * Substitui as variáveis pelos valores. Usado na pré-visualização do painel e,
 * mais tarde, para registrar em `Message.body` o texto que a pessoa de fato leu
 * — o que a Meta guarda é só o id do template.
 */
export function preencher(
  texto: string | null | undefined,
  valores: Record<string, string | null | undefined>,
): string {
  if (!texto) return '';

  return texto.replace(VARIAVEL, (original, nome: string) => {
    const valor = valores[nome];
    return valor === undefined || valor === null || valor === '' ? original : valor;
  });
}

/**
 * Regras da Meta que dá para verificar ANTES de submeter.
 *
 * Existe porque a submissão é assíncrona e lenta: o template entra em análise,
 * volta rejeitado horas ou dias depois, e o motivo que a Meta devolve é quase
 * sempre `INVALID_FORMAT` — sem dizer onde. Cada uma destas regras já custou
 * essa espera a alguém.
 *
 * Devolve a lista de problemas em português, vazia quando está tudo certo.
 */
export function validarTemplate(partes: PartesTemplate): string[] {
  const problemas: string[] = [];
  const body = (partes.body ?? '').trim();

  if (!body) {
    problemas.push('O corpo da mensagem não pode ficar vazio.');
  }
  if (body.length > LIMITES_TEMPLATE.body) {
    problemas.push(`O corpo passa de ${LIMITES_TEMPLATE.body} caracteres (tem ${body.length}).`);
  }

  // ── Cabeçalho ────────────────────────────────────────────────────────────
  const header = (partes.header ?? '').trim();
  if (header) {
    if (header.length > LIMITES_TEMPLATE.header) {
      problemas.push(`O cabeçalho passa de ${LIMITES_TEMPLATE.header} caracteres.`);
    }
    if (extrairVariaveis(header).length > 1) {
      problemas.push('O cabeçalho aceita no máximo uma variável.');
    }
    if (/\n/.test(header)) {
      problemas.push('O cabeçalho não pode ter quebra de linha.');
    }
  }

  // ── Rodapé ───────────────────────────────────────────────────────────────
  const footer = (partes.footer ?? '').trim();
  if (footer) {
    if (footer.length > LIMITES_TEMPLATE.footer) {
      problemas.push(`O rodapé passa de ${LIMITES_TEMPLATE.footer} caracteres.`);
    }
    if (extrairVariaveis(footer).length) {
      problemas.push('O rodapé não aceita variáveis.');
    }
  }

  // ── Variáveis do corpo ───────────────────────────────────────────────────
  problemas.push(...problemasDeVariaveis(body));

  // ── Botões ───────────────────────────────────────────────────────────────
  const botoes = partes.botoes ?? [];
  if (botoes.length > LIMITES_TEMPLATE.botoes) {
    problemas.push(`No máximo ${LIMITES_TEMPLATE.botoes} botões.`);
  }
  for (const botao of botoes) {
    if (!botao.texto?.trim()) {
      problemas.push('Todo botão precisa de texto.');
    } else if (botao.texto.length > LIMITES_TEMPLATE.textoBotao) {
      problemas.push(
        `O texto do botão "${botao.texto.slice(0, 15)}…" passa de ${LIMITES_TEMPLATE.textoBotao} caracteres.`,
      );
    }
    if (botao.tipo !== 'RESPOSTA_RAPIDA' && !botao.valor?.trim()) {
      problemas.push(
        `O botão "${botao.texto}" é do tipo ${botao.tipo} e precisa do endereço ou telefone.`,
      );
    }
  }

  return problemas;
}

function problemasDeVariaveis(body: string): string[] {
  const problemas: string[] = [];

  /**
   * `{{ }}` que não casou com o padrão de nome.
   *
   * Pega principalmente quem escreveu `{{1}}` por hábito da Meta: passaria
   * intacto pela conversão, a Meta esperaria um parâmetro, e o envio quebraria
   * com 132000 — em produção, não aqui.
   */
  const nomeValido = new RegExp(VARIAVEL.source); // sem o "g": `test` aqui não pode ter estado
  const naoReconhecidas = (body.match(CHAVES) ?? []).filter((t) => !nomeValido.test(t));
  for (const sobra of naoReconhecidas) {
    problemas.push(
      `"${sobra}" não é uma variável válida. Use {{nome_assim}} — letras, números e "_".`,
    );
  }

  const ocorrencias = [...body.matchAll(VARIAVEL)];

  const repetidas = ocorrencias
    .map((m) => m[1]!)
    .filter((nome, i, todas) => todas.indexOf(nome) !== i)
    .filter((nome, i, todas) => todas.indexOf(nome) === i);

  /**
   * Variável repetida vira índice posicional repetido, e a Meta recusa.
   *
   * Reaproveitar o mesmo `{{1}}` duas vezes é o caminho óbvio, e é justamente o
   * que ela não aceita. Barrar aqui custa uma reescrita de segundos; descobrir
   * na análise custa dias.
   */
  for (const nome of repetidas) {
    problemas.push(
      `A variável {{${nome}}} aparece mais de uma vez. A Meta exige uma ocorrência por variável — ` +
        'reescreva a frase ou crie outra variável.',
    );
  }

  const semEspaco = body.trim();
  if (ocorrencias.length) {
    if (/^\{\{\s*[a-zA-Z]/.test(semEspaco)) {
      problemas.push('A mensagem não pode COMEÇAR com uma variável.');
    }
    if (/[a-zA-Z0-9_]\s*\}\}$/.test(semEspaco)) {
      problemas.push('A mensagem não pode TERMINAR com uma variável.');
    }
    if (/\}\}[\s\p{P}]{0,2}\{\{/u.test(semEspaco)) {
      problemas.push('Duas variáveis não podem ficar coladas — escreva algum texto entre elas.');
    }
  }

  return problemas;
}

/**
 * Exemplos plausíveis para as variáveis que o projeto usa sempre.
 *
 * Mora aqui porque servem a dois lados que precisam concordar: a
 * pré-visualização no painel e o `example` que vai para a Meta na submissão. Se
 * fossem duas listas, o operador aprovaria na tela um texto e mandaria outro.
 */
export const EXEMPLOS_PADRAO: Record<string, string> = {
  nome: 'Ana',
  primeiro_nome: 'Ana',
  nome_participante: 'Ana Souza',
  nome_evento: 'Encontro de Líderes 2026',
  evento: 'Encontro de Líderes 2026',
  /**
   * Quem organiza — nome fantasia, nunca razão social.
   *
   * O exemplo é curto e sem "LTDA" de propósito: é ele que a Meta vê na
   * análise, e é ele que aparece na prévia. Um exemplo com a razão social
   * ensinaria o formato errado a quem estiver escrevendo o texto.
   */
  nome_organizacao: 'Jandaia Eventos',
  organizacao: 'Jandaia Eventos',
  /** A empresa do PARTICIPANTE. A de quem organiza é `nome_organizacao`. */
  empresa: 'Acme Indústria',
  cargo: 'Diretora de Operações',
  cidade: 'São Paulo',
  data: '12 de março',
  hora: '14h30',
  local: 'Centro de Convenções',
  nome_recomendado: 'Carlos Menezes',
  primeiro_nome_recomendado: 'Carlos',
  telefone_recomendado: '+55 47 90000-0000',
  motivo: 'os dois trabalham com cadeia de suprimentos',
  quantidade_enviada: '3',
  /** URL do Aviso de Privacidade da organização, exigido no consentimento. */
  link_privacidade: 'exemplo.com.br/privacidade',
};

/**
 * Exemplo de uma variável: o informado, o padrão, ou o próprio nome legível.
 *
 * A Meta REJEITA template com variável e sem exemplo, e a mensagem que devolve
 * não diz que foi isso. Um exemplo fraco é melhor do que três dias de espera
 * para descobrir o que faltava.
 */
export function exemploDe(
  nome: string,
  informados?: Record<string, string | null | undefined> | null,
): string {
  const informado = informados?.[nome]?.trim();
  if (informado) return informado;

  return EXEMPLOS_PADRAO[nome] ?? nome.replace(/_/g, ' ');
}

/**
 * Nome do template na Meta: minúsculas, números e "_".
 *
 * Único por WABA e por idioma. Deriva do nome interno para que ninguém precise
 * inventar dois nomes para a mesma coisa.
 */
export function metaNameDe(nomeInterno: string): string {
  return nomeInterno
    // NFD separa a letra do acento; o intervalo seguinte remove só o acento.
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

export const META_NAME_VALIDO = /^[a-z0-9_]{1,512}$/;
